import {
  UserdataResponse,
  UserInfoRecord,
  AppInfoRecord,
  ExamRecord,
} from "./types";
import axios from "axios";
import { ApiEndpoint } from "./utils";

const userLoggedIn = (): boolean => {
  const currentUser = localStorage.getItem("fcp_user");
  if (currentUser) {
    const resp = JSON.parse(currentUser) as unknown as UserdataResponse;
    return resp?.userid && resp.userType !== -1 ? true : false;
  } else {
    return false;
  }
};

const logoutUser = (): void => {
  localStorage.removeItem("fcp_user");
  localStorage.removeItem("fcp_app_info");
};

const getUserInfo = (): UserInfoRecord | null => {
  const userData = localStorage.getItem("fcp_user");
  return userData ? JSON.parse(userData) : null;
};

const getAppInfo = (): AppInfoRecord | null => {
  const appInfo = localStorage.getItem("fcp_app_info");
  return appInfo ? JSON.parse(appInfo) : null;
};

const setAppInfo = (data: AppInfoRecord): void => {
  const appInfo = getAppInfo();
  localStorage.setItem("fcp_app_info", JSON.stringify({ ...appInfo, ...data }));
};

const setUserInfo = async (
  data: UserInfoRecord,
  initial = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  if (initial) {
    localStorage.setItem("fcp_user", JSON.stringify(data));
  } else {
    const userData = getUserInfo();
    localStorage.setItem("fcp_user", JSON.stringify({ ...userData, ...data }));
    axios.post(ApiEndpoint + "wp-json/satapp-api/v1/updateUserData", {
      token: userData?.token,
      userData: data,
    });
  }
};

const updatePaidExamStatus = async (examId: number): Promise<void> => {
  const appInfo = getAppInfo() as AppInfoRecord;
  const uData = getUserInfo();
  const examList = appInfo?.examData as ExamRecord[];

  examList?.forEach((exam) => {
    if (exam.examId === examId) {
      exam.paid = true;
    }
    return exam;
  });

  if (appInfo) {
    appInfo.examData = examList;
    setAppInfo(appInfo);
  }
  await axios.post(`${ApiEndpoint}wp-json/satapp-api/v1/updateExamPaid`, {
    token: uData?.token,
    examId: examId,
    value: 1,
  });
};

const updateCurrentExamData = async (
  examId: number | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields: any,
  init = false
): Promise<void> => {
  const appInfo = getAppInfo() as AppInfoRecord;
  const uData = getUserInfo();

  appInfo.currentExamData = !init
    ? { ...appInfo.currentExamData, ...fields }
    : fields;
  setAppInfo(appInfo);

  if (init) return;

  await axios.post(`${ApiEndpoint}wp-json/satapp-api/v1/updateUserExam`, {
    token: uData?.token,
    exam_id: examId,
    userData: fields,
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const updateLocalExamData = (fields: any): void => {
  const appInfo = getAppInfo() as AppInfoRecord;
  appInfo.currentExamData = { ...appInfo.currentExamData, ...fields };
  setAppInfo(appInfo); 
};

export const getCurrentSectionandQuestion = (): {
  currentSectionId: number | undefined;
  currentQuestionId: string | number | null | undefined;
} => {
  const appInfo = getAppInfo() as AppInfoRecord;
  let currentSectionId = appInfo?.currentExamData?.currentSectionId;
  const lastQuestionAnswered = appInfo?.currentExamData?.lastQuestionAnswered;

  currentSectionId =
    typeof currentSectionId == "string"
      ? parseInt(currentSectionId)
      : currentSectionId;

  let tempID = "";
  if (appInfo && appInfo.currentExamData && currentSectionId) {
    tempID = appInfo?.currentExamData.examAnswers[currentSectionId].questions[0]
      .questionId as string;
  }

  const currentQuestionId = lastQuestionAnswered
    ? lastQuestionAnswered
    : tempID;

  return { currentSectionId, currentQuestionId };
};

export const sanitizeServerData = (data: object): object => {
  interface TempObject {
    [key: string]: string | number;
  }

  const temp: TempObject = { ...data };
  const stringKeys = [
    "examType",
    "minutesRemaining",
    "retryAttempts",
    "satMathLevel",
    "satVerbalLevel",
    "sectionId",
    "sectionStartTime",
  ];
  stringKeys.forEach((key) => {
    const item = temp[key as keyof typeof temp];
    temp[key] = typeof item === "string" ? parseInt(item) : item;
  });

  return temp;
};

export const finishExamSection = async () => {
  const { currentSectionId } = getCurrentSectionandQuestion();
  const examData = getAppInfo()?.currentExamData;
  const appInfo = getAppInfo();
  const SATVerbalMin = 70;
  const SATMathMin = 65;
  let targetSection = -1;

  if (currentSectionId === 0 || currentSectionId === 3) {
    const resp = await axios.post(
      `${ApiEndpoint}wp-json/satapp-api/v1/calculateSectionScore`,
      {
        token: getUserInfo()?.token,
        exam_id: examData?.examId,
        sectionId: currentSectionId,
      }
    );
    if (currentSectionId === 0) {
      if (resp.data.score < SATVerbalMin) {
        targetSection = 1;
      } else {
        targetSection = 2;
      }
    }
    if (currentSectionId === 3) {
      if (resp.data.score < SATMathMin) {
        targetSection = 4;
      } else {
        targetSection = 5;
      }
    }
  } else {
    targetSection = currentSectionId ? currentSectionId + 1 : -1;
  }

  const nextSectionId =
    currentSectionId !== undefined ? targetSection : (-1 as number);
  const examType = getAppInfo()?.currentExamData?.examType;
  const time = examType === 0 ? "standardTime" : "extendedTime";
  const minutesRemaining =
    appInfo && appInfo.configData ? appInfo.configData[time][nextSectionId] : 0;

  updateCurrentExamData(
    examData?.examId as number,
    {
      currentSectionId: nextSectionId,
      lastQuestionAnswered: examData
        ? examData.examAnswers[nextSectionId].questions[0].questionId
        : null,
      minutesRemaining: minutesRemaining,
    },
    false
  );
};

export {
  userLoggedIn,
  getUserInfo,
  logoutUser,
  setUserInfo,
  setAppInfo,
  getAppInfo,
  updatePaidExamStatus,
  updateCurrentExamData,
  updateLocalExamData,
};
